import leaflet, { FeatureGroup, LatLngBounds, LatLngBoundsLiteral, LatLngExpression, Layer, Map, Popup, PopupOptions, Rectangle, rectangle } from 'leaflet';
import { cloneDeep } from 'lodash';
import { ReactElement } from 'react';
import { renderToString } from 'react-dom/server';
import { DEFAULT_STROKE_OPACITY } from '../../../../helpers/global.constants';
import { ISegmentation } from '../../annotations.interface';
import { IQuestionModel } from '../../question.model';

interface INewPopupOptions extends PopupOptions {
  storeKey?: string;
  segmentation: ISegmentation;
}

export class AttributePopup extends Popup {
  private readonly FRAME_PADDING_POINT: number = 10;

  _container!: HTMLElement;
  _latlng!: LatLngExpression;
  _contentNode!: HTMLElement;
  _width: number = 0;
  _content: string = '';
  _group!: FeatureGroup;
  _showOpacity: boolean;
  _frame?: Rectangle;
  _segmentation: ISegmentation;
  _bbox: LatLngBounds;
  _selectedAttributes: string[];
  _showHeaders: boolean;

  constructor(segmentation: ISegmentation, map: Map, featureGroup: FeatureGroup, showOpacity: boolean, showHeaders: boolean, selectedAttributes: string[]) {
    super({ segmentation } as INewPopupOptions);

    this._map = map;
    this._group = featureGroup;
    this._showOpacity = showOpacity;
    this._segmentation = segmentation;
    this._selectedAttributes = selectedAttributes;
    this._showHeaders = showHeaders;
    this._bbox = cloneDeep(this._segmentation.bbox);
    this.openOn(this._map);
    this.addTo(this._group);
  }

  initialize(options: INewPopupOptions, source: Layer): void {
    const initailizeOptions = {} as INewPopupOptions;
    initailizeOptions.className = 'attribute-popup';
    initailizeOptions.autoClose = false;
    initailizeOptions.closeOnClick = false;
    initailizeOptions.closeButton = false;
    initailizeOptions.storeKey = options.segmentation.feature.id;

    super.initialize(initailizeOptions, source);
  }

  onRemove(map: Map): this {
    this._frame?.removeFrom(map);
    super.onRemove(map);
    return this;
  }

  _updateContent(): void {
    const bounds = this.getBounds();
    this._latlng = bounds.getNorthWest();

    const attributeContent = this.getAttributeContentHtml(this._segmentation.questions);
    this._frame?.removeFrom(this._map);

    if (attributeContent) {
      this._frame = rectangle(bounds, { dashArray: '3, 3', opacity: this._showOpacity ? DEFAULT_STROKE_OPACITY : 1, fill: false, weight: 1, color: '#fff' });
      this._frame.addTo(this._group);
    }

    this._content = attributeContent ?? '<div class="attributes"></div>';
    super._updateContent();
  }

  _updatePosition(): void {
    if (!this._map) {
      return;
    }

    const pos = this._map.latLngToLayerPoint(this._latlng);
    const bounds = this.getBounds();
    this._width = this._map.latLngToLayerPoint(bounds.getNorthWest()).distanceTo(this._map.latLngToLayerPoint(bounds.getNorthEast()));

    this._contentNode.style.width = 'unset';
    this._contentNode.style.minWidth = `${this._width}px`;
    this._contentNode.style.pointerEvents = 'none';
    this._contentNode.style.userSelect = 'none';

    this._container.style.top = `${pos.y}px`;
    this._container.style.left = `${pos.x - 1}px`;
    if (this._showOpacity) {
      this._container.style.opacity = `${DEFAULT_STROKE_OPACITY}`;
    }

    this._container.oncontextmenu = (e: Event): void => {
      e.preventDefault();
    };
  }

  updateData(showOpacity: boolean, hover: boolean, showHeaders: boolean, selectedAttributes: string[], segmentation: ISegmentation | undefined): void {
    this._segmentation = segmentation ?? this._segmentation;
    this._showOpacity = showOpacity;
    this._selectedAttributes = selectedAttributes;
    this._showHeaders = showHeaders;
    this._container.style.opacity = this._showOpacity ? `${DEFAULT_STROKE_OPACITY}` : '1';
    this._container.style.zIndex = hover ? '10000' : 'auto';
    this._updateContent();
  }

  getBounds(): LatLngBounds {
    const bounds = leaflet.geoJSON(this._segmentation.feature.geometry).getBounds();
    const framePadding =
      this._segmentation.feature.geometry.type === 'Point' || this._segmentation.feature.geometry.type === 'LineString' ? this.FRAME_PADDING_POINT : 0;

    const extended: LatLngBoundsLiteral = [
      [bounds.getSouth() - framePadding, bounds.getWest() - framePadding],
      [bounds.getNorth() + framePadding, bounds.getEast() + framePadding],
    ];

    return new LatLngBounds(extended);
  }

  getAttributeContentHtml = (questions: IQuestionModel[]): string | undefined => {
    const getAnswers = (item: IQuestionModel): string =>
      item.answer ||
      item.answers
        .filter((x) => x.selected)
        .map((x) => x.text)
        .join(', ') ||
      '-';

    const getAttributes = (): ReactElement | undefined => {
      const selected = questions.filter((x) => this._selectedAttributes.includes(x.id));

      if (!selected.length) {
        return undefined;
      }

      return (
        <div className='attributes'>
          {selected.map((item) => (
            <div
              className='attribute'
              key={item.id}
            >
              {this._showHeaders && (
                <>
                  <span className='attribute-title'>{item.text}</span>
                  <span>:</span>
                </>
              )}
              <span className='attribute-answer'>{getAnswers(item)}</span>
            </div>
          ))}
        </div>
      );
    };

    const attributes = getAttributes();

    if (!attributes) {
      return undefined;
    }

    return renderToString(attributes);
  };
}
